import {
  COMPANY,
  COOKIE_CHOICE_MONTHS,
  LOG_RETENTION_DAYS,
  META_PIXEL_PARTNER,
  PAYMENT_PROCESSOR,
  PRIVACY_LAST_UPDATED,
  REDRESS,
  SUBPROCESSORS,
} from "../../constants/legal";

/**
 * The text of the Terms & Conditions and the Privacy Policy, in Romanian and
 * English.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT LEGAL ADVICE. This is a structured draft covering the sections Romanian
 * and EU law require of an online platform — identification (Legea 365/2002),
 * consumer information and withdrawal (OUG 34/2014), GDPR art. 13 information
 * and art. 28 processor clauses. It is written to be reviewed, not to be shipped
 * unread: have a Romanian lawyer go over it, and verify every authority name,
 * address and URL before launch.
 *
 * Every decision it used to leave open has been made and written in — payment
 * terms, refunds, sub-processors, retention, redress. Nothing here may ship as
 * a placeholder: this text is served to the public, so a note-to-self left in
 * it is published advice to nobody, and a reviewer reading "complete the refund
 * policy" stops reading there.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Romanian is the governing version — consumers on the Romanian market are
 * entitled to be informed in Romanian, so the English text is a convenience
 * translation and says so.
 *
 * Shape: `sections: [{ heading, body: [...] }]`, where a body item is either a
 * paragraph string or `{ list: [...] }`. Rendered by LegalPage.jsx.
 */

const identity = {
  ro: `${COMPANY.legalName}, cu sediul social în ${COMPANY.registeredOffice}, înregistrată la Registrul Comerțului sub nr. ${COMPANY.tradeRegisterNumber}, cod unic de înregistrare ${COMPANY.taxId}, e-mail ${COMPANY.email}, telefon ${COMPANY.phone}`,
  en: `${COMPANY.legalName}, with its registered office at ${COMPANY.registeredOffice}, registered with the Romanian Trade Register under no. ${COMPANY.tradeRegisterNumber}, tax identification number ${COMPANY.taxId}, e-mail ${COMPANY.email}, telephone ${COMPANY.phone}`,
};

const dataContact = {
  ro: COMPANY.dpoEmail || COMPANY.email,
  en: COMPANY.dpoEmail || COMPANY.email,
};

/**
 * Where the Meta Pixel runs, said the same way in the Privacy Policy and the
 * Cookie Policy. It mirrors TRACKED_PAGES in utils/metaPixel.js — change one
 * and the other is wrong.
 */
const pixelScope = {
  ro: `numai pe paginile de prezentare ${COMPANY.brandName} — prima pagină, căutarea afacerilor, înregistrarea și autentificarea, documentele legale. Nu rulează pe paginile de rezervare ale afacerilor care folosesc platforma, în panoul de administrare, în formularul de rezervare inclus pe alte site-uri și nici pe paginile deschise din linkurile primite pe e-mail`,
  en: `only on ${COMPANY.brandName}'s own pages — the home page, the business search, sign-up and sign-in, the legal documents. It does not run on the booking pages of businesses using the platform, in the dashboard, in the booking form embedded on other websites, or on pages opened from links received by e-mail`,
};

/** The essence of the joint-controller arrangement with Meta, GDPR art. 26(2). */
const metaJointControl = {
  ro: `Meta nu este împuternicitul nostru: pentru colectarea datelor prin pixel și transmiterea lor către Meta suntem operatori asociați (art. 26 GDPR), potrivit Addendumului Meta pentru operatori (${META_PIXEL_PARTNER.controllerAddendum}). Esențialul acordului: noi răspundem de obținerea consimțământului și de informarea dumneavoastră, Meta de securitatea datelor pe care le primește. Ce face Meta mai departe cu ele — inclusiv afișarea de reclame și îmbunătățirea propriilor servicii — face ca operator independent, potrivit politicii sale de confidențialitate (${META_PIXEL_PARTNER.privacyPolicy}). Vă puteți exercita drepturile față de oricare dintre noi.`,
  en: `Meta is not our processor: for collecting data through the pixel and transmitting it to Meta we are joint controllers (art. 26 GDPR), under Meta's Controller Addendum (${META_PIXEL_PARTNER.controllerAddendum}). The essence of the arrangement: we are responsible for obtaining your consent and informing you, Meta for the security of the data it receives. What Meta does with the data afterwards — including showing ads and improving its own services — it does as an independent controller, under its own privacy policy (${META_PIXEL_PARTNER.privacyPolicy}). You may exercise your rights against either of us.`,
};

const metaTransfer = {
  ro: `Meta poate transfera datele către **Meta Platforms, Inc.**, în Statele Unite. Transferul se întemeiază pe Cadrul UE-SUA privind confidențialitatea datelor, la care Meta Platforms, Inc. este certificată, și pe clauzele contractuale standard adoptate de Comisia Europeană.`,
  en: `Meta may transfer the data to **Meta Platforms, Inc.**, in the United States. The transfer relies on the EU-U.S. Data Privacy Framework, under which Meta Platforms, Inc. is certified, and on the standard contractual clauses adopted by the European Commission.`,
};

export const termsContent = {
  ro: {
    title: "Termeni și Condiții",
    intro: `Vă rugăm să citiți acest document înainte de a vă crea un cont. Prin bifarea casetei de acceptare la înregistrare, încheiați un contract cu noi în termenii de mai jos. Versiunea în limba română este cea care guvernează raportul contractual.`,
    sections: [
      {
        heading: "1. Cine suntem",
        body: [
          `Platforma ${COMPANY.brandName} („Platforma”) este operată de ${identity.ro} („noi”, „Furnizorul Platformei”).`,
          `Pentru orice întrebare legată de acest document, ne puteți scrie la ${COMPANY.email}.`,
        ],
      },
      {
        heading: "2. Definiții",
        body: [
          {
            list: [
              `**Platforma** – aplicația web ${COMPANY.brandName}, prin care se pot publica disponibilități și efectua programări.`,
              `**Utilizator** – orice persoană care își creează un cont pe Platformă.`,
              `**Client** – Utilizatorul care folosește Platforma pentru a face programări la un Furnizor de Servicii, în calitate de consumator.`,
              `**Furnizor de Servicii** – Utilizatorul profesionist (persoană fizică autorizată sau juridică) care își publică pe Platformă serviciile și programul și primește programări.`,
              `**Rezervare** – programarea efectuată de un Client la un Furnizor de Servicii, prin Platformă.`,
              `**Conținut al Utilizatorului** – datele introduse de Utilizator: denumiri de servicii, prețuri, descrieri, imagini, program de lucru.`,
            ],
          },
        ],
      },
      {
        heading: "3. Obiectul contractului și rolul nostru",
        body: [
          `Vă punem la dispoziție un instrument tehnic prin care Furnizorii de Servicii își gestionează programul și primesc rezervări, iar Clienții găsesc și rezervă intervale disponibile.`,
          `**Nu prestăm serviciile care se rezervă prin Platformă și nu suntem parte în contractul dintre Client și Furnizorul de Servicii.** Serviciul rezervat este prestat exclusiv de Furnizorul de Servicii, pe răspunderea acestuia. Nu garantăm calitatea, legalitatea, siguranța sau prețul serviciilor publicate de Furnizorii de Servicii și nu răspundem pentru neexecutarea sau executarea defectuoasă a acestora.`,
          `Reclamațiile privind serviciul prestat se adresează direct Furnizorului de Servicii. Reclamațiile privind funcționarea Platformei ni se adresează nouă.`,
          `Avem deci **două roluri diferite**, iar distincția contează pentru cine răspunde și cine facturează: față de rezervări suntem **intermediar tehnic**, nu comerciant, iar față de abonamentele la Platformă suntem **comerciant** — noi vindem accesul, noi îl încasăm și noi emitem factura, în nume propriu. Nu suntem marketplace: nu se vând bunuri sau servicii ale terților prin Platformă și nu încasăm bani în numele Furnizorilor de Servicii.`,
        ],
      },
      {
        heading: "4. Eligibilitate și crearea contului",
        body: [
          `Pentru a vă crea un cont trebuie să aveți cel puțin 16 ani. Dacă aveți sub 16 ani, contul poate fi creat numai de titularul autorității părintești, în numele dumneavoastră.`,
          `Vă obligați să furnizați date reale, complete și actuale, și să le actualizați când se schimbă. Un cont creat cu date false poate fi suspendat.`,
          `Contul devine utilizabil numai după ce confirmați adresa de e-mail prin linkul de activare pe care îl trimitem. Linkul este valabil 24 de ore.`,
          `Sunteți responsabil pentru păstrarea confidențialității parolei și pentru orice activitate desfășurată din contul dumneavoastră. Dacă bănuiți o utilizare neautorizată, anunțați-ne imediat la ${COMPANY.email}.`,
          `Un cont este personal. Nu îl transmiteți și nu îl puneți la dispoziția altei persoane.`,
        ],
      },
      {
        heading: "5. Utilizări interzise",
        body: [
          `Vă obligați să nu:`,
          {
            list: [
              `încărcați conținut ilegal, înșelător, defăimător sau care încalcă drepturile altcuiva;`,
              `folosiți Platforma pentru a face rezervări false, în serie, sau pentru a bloca disponibilitatea altor utilizatori;`,
              `încercați să accesați conturi, date sau zone ale Platformei la care nu aveți drept de acces;`,
              `extrageți automatizat date din Platformă (scraping), ocoliți măsurile de securitate sau limitele tehnice;`,
              `încărcați cod dăunător ori interferați cu funcționarea Platformei;`,
              `folosiți datele altor utilizatori în alt scop decât cel pentru care v-au fost puse la dispoziție.`,
            ],
          },
          `Încălcarea acestor obligații poate duce la suspendarea sau închiderea contului, potrivit secțiunii 12.`,
        ],
      },
      {
        heading: "6. Rezervări — dispoziții pentru Clienți",
        body: [
          `Rezervarea făcută prin Platformă reprezintă o solicitare adresată Furnizorului de Servicii, care poate fi confirmată, modificată sau refuzată de acesta potrivit propriei politici.`,
          `Condițiile de anulare, reprogramare, plată și eventualele penalități sunt stabilite de fiecare Furnizor de Servicii și vă sunt comunicate de acesta. Vă recomandăm să le verificați înainte de a rezerva.`,
          `Anularea unei rezervări prin Platformă, atunci când această opțiune este disponibilă, nu vă scutește de obligațiile asumate față de Furnizorul de Servicii dacă politica acestuia prevede altfel.`,
        ],
      },
      {
        heading:
          "7. Abonamente și plăți — dispoziții pentru Furnizorii de Servicii",
        body: [
          `Accesul Furnizorilor de Servicii la funcționalitățile Platformei se face pe bază de abonament, în planul și la prețul afișate la momentul subscrierii. Prețurile sunt exprimate în EUR și nu includ TVA.`,
          `Fiecare plan include limite (număr de resurse, servicii, rezervări). La atingerea unei limite, operațiunea este refuzată cu un mesaj explicit, iar planul poate fi schimbat oricând.`,
          `Plata se face online, cu cardul, prin ${PAYMENT_PROCESSOR.name}. Prețul este listat în EUR fără TVA și este **încasat în lei**, la cursul BNR din ziua plății și cu TVA inclus; suma exactă în lei, cursul folosit și data lui vă sunt arătate în aplicație înainte de a fi trimis către formularul de card. Datele cardului nu ajung la noi în niciun moment — sunt introduse pe pagina procesatorului.`,
          `Factura este emisă de ${COMPANY.legalName} și vă este trimisă automat, în PDF, pe adresa de e-mail a contului. O regăsiți oricând în secțiunea Facturare din cont.`,
          `Abonamentul se plătește pentru o perioadă, iar la finalul ei **nu se reînnoiește automat**. Nu facem nicio retragere de bani pe care să nu o porniți dumneavoastră: ca să continuați, inițiați o nouă plată din cont. Accesul la funcționalitățile plătite ține până la data afișată acolo.`,
          `Renunțarea nu cere preaviz și nu costă nimic — lăsați perioada plătită să se încheie. **Sumele plătite pentru o perioadă începută nu se restituie**, pentru că serviciul a fost pus la dispoziție pentru toată perioada respectivă. Fac excepție dreptul de retragere de la secțiunea 9 și situațiile în care serviciul nu a fost disponibil din vina noastră.`,
          `Neplata abonamentului la scadență poate duce la restrângerea accesului la funcționalitățile plătite, cu păstrarea datelor pentru o perioadă rezonabilă înainte de ștergere.`,
        ],
      },
      {
        heading:
          "8. Prelucrarea datelor clienților Furnizorului de Servicii (clauze de împuternicire)",
        body: [
          `Această secțiune se aplică exclusiv Furnizorilor de Servicii și constituie contractul de prelucrare cerut de art. 28 din Regulamentul (UE) 2016/679 („GDPR”).`,
          `În privința datelor persoanelor care fac rezervări la dumneavoastră, **dumneavoastră sunteți operator, iar noi acționăm ca împuternicit**. Dumneavoastră stabiliți scopurile prelucrării și răspundeți de existența unei baze legale pentru datele pe care le introduceți sau ni le transmiteți, inclusiv pentru datele pe care le adăugați manual în numele clienților dumneavoastră.`,
          `În această calitate ne obligăm să:`,
          {
            list: [
              `prelucrăm datele numai pentru a vă furniza Platforma și numai potrivit instrucțiunilor dumneavoastră;`,
              `asigurăm confidențialitatea, prin persoane ținute de o obligație de confidențialitate;`,
              `aplicăm măsuri tehnice și organizatorice adecvate potrivit art. 32 GDPR;`,
              `nu angajăm alți subîmputerniciți fără a vă informa în prealabil, dându-vă posibilitatea de a obiecta; lista subîmputerniciților actuali este disponibilă la cerere;`,
              `vă asistăm, în limita rezonabilului, în răspunsul la cererile persoanelor vizate și în îndeplinirea obligațiilor privind securitatea și evaluările de impact;`,
              `vă notificăm fără întârziere nejustificată orice încălcare a securității datelor de care luăm cunoștință;`,
              `la încetarea contractului, ștergem sau vă returnăm datele, la alegerea dumneavoastră, cu excepția celor pe care legea ne obligă să le păstrăm;`,
              `vă punem la dispoziție informațiile necesare pentru a demonstra respectarea acestor obligații.`,
            ],
          },
          `Durata prelucrării este durata contractului. Categoriile de date și de persoane vizate sunt cele determinate de utilizarea Platformei: date de identificare și de contact ale clienților dumneavoastră și istoricul rezervărilor.`,
          `Subîmputerniciții pe care îi folosim la data acestei versiuni sunt cei enumerați în Politica de Confidențialitate, punctul 5, împreună cu rolul fiecăruia și locul prelucrării. Vă anunțăm înainte de a adăuga altul și puteți obiecta.`,
        ],
      },
      {
        heading: "9. Dreptul de retragere — pentru consumatori",
        body: [
          `Dacă sunteți consumator, aveți dreptul de a vă retrage din contractul încheiat la distanță cu noi în termen de 14 zile de la încheierea acestuia, fără a fi nevoie să vă justificați decizia, potrivit OUG nr. 34/2014.`,
          `**Vă retrageți online**, completând formularul de la ${REDRESS.withdrawalPath} — este calea pe care v-o punem la dispoziție potrivit art. 11^1 din OUG nr. 34/2014. Primiți pe loc o confirmare pe e-mail, cu data la care am înregistrat cererea.`,
          `Rămâne valabilă și o notificare neechivocă trimisă la ${COMPANY.email}, ori folosirea formularului-tip din anexa la ordonanță. Nu vă cerem să justificați decizia.`,
          `Restituim suma încasată pe același card, în cel mult 14 zile de la primirea cererii.`,
          `Atenție: dacă solicitați începerea prestării serviciului înainte de expirarea celor 14 zile și confirmați că înțelegeți că veți pierde dreptul de retragere după executarea completă, dreptul de retragere se stinge la momentul executării integrale, potrivit art. 16 lit. a) din OUG nr. 34/2014.`,
          `Crearea unui cont gratuit nu presupune plata unui preț; retragerea în acest caz echivalează cu ștergerea contului, pe care o puteți solicita oricând.`,
        ],
      },
      {
        heading: "10. Proprietate intelectuală",
        body: [
          `Platforma, codul, structura, denumirea și elementele grafice ne aparțin sau le folosim în baza unei licențe. Nu dobândiți niciun drept asupra lor prin utilizarea Platformei, în afara dreptului de a o folosi potrivit acestor Termeni.`,
          `Conținutul pe care îl încărcați rămâne al dumneavoastră. Ne acordați o licență neexclusivă, limitată la ceea ce este necesar pentru a găzdui, afișa și transmite acel conținut în cadrul Platformei, pe durata cât îl păstrați încărcat.`,
          `Declarați că aveți drepturile necesare asupra conținutului încărcat, inclusiv asupra imaginilor.`,
        ],
      },
      {
        heading: "11. Disponibilitate și limitarea răspunderii",
        body: [
          `Depunem eforturi rezonabile pentru ca Platforma să fie disponibilă și funcțională, dar nu garantăm funcționarea neîntreruptă sau lipsită de erori. Putem întrerupe temporar accesul pentru mentenanță, anunțând în prealabil când este posibil.`,
          `Nu răspundem pentru prejudicii rezultate din: serviciile prestate de Furnizorii de Servicii; conținutul încărcat de utilizatori; utilizarea contului de către o persoană care a obținut parola din culpa dumneavoastră; cauze care nu ne sunt imputabile, inclusiv defecțiuni ale rețelelor de comunicații.`,
          `Nimic din acest document nu limitează răspunderea noastră în cazurile în care legea nu permite limitarea, inclusiv pentru prejudicii cauzate cu intenție sau din culpă gravă, și nu afectează drepturile pe care legislația privind protecția consumatorilor le acordă consumatorilor.`,
        ],
      },
      {
        heading: "12. Suspendarea și încetarea",
        body: [
          `Vă puteți închide contul oricând, prin cerere la ${COMPANY.email}.`,
          `Putem suspenda sau închide un cont dacă acesta încalcă secțiunea 5, dacă utilizarea prejudiciază alți utilizatori sau Platforma, ori dacă suntem obligați legal. Când împrejurările permit, vă anunțăm în prealabil și vă dăm posibilitatea de a remedia situația.`,
          `La încetare, datele se șterg sau se anonimizează potrivit Politicii de Confidențialitate, cu excepția celor pe care legea ne obligă să le păstrăm.`,
        ],
      },
      {
        heading: "13. Modificarea Termenilor",
        body: [
          `Putem modifica acești Termeni, de exemplu când adăugăm funcționalități sau când se schimbă legislația. Fiecare versiune are un număr și o dată, afișate în capul documentului.`,
          `Vă anunțăm modificările substanțiale prin e-mail sau printr-o notificare în aplicație, cu cel puțin 15 zile înainte de intrarea lor în vigoare. Dacă nu sunteți de acord, puteți închide contul înainte de această dată; continuarea utilizării după intrarea în vigoare înseamnă acceptarea noii versiuni.`,
          `Păstrăm evidența versiunii pe care ați acceptat-o la înregistrare și a momentului acceptării.`,
        ],
      },
      {
        heading: "14. Legea aplicabilă și soluționarea litigiilor",
        body: [
          `Acestor Termeni se aplică legea română. Dacă sunteți consumator, vă păstrați beneficiul dispozițiilor imperative din statul în care aveți reședința obișnuită.`,
          `Ne puteți sesiza oricând direct, la ${COMPANY.email}; încercăm să răspundem în cel mult 30 de zile.`,
          `Dacă sunteți consumator, vă puteți adresa Autorității Naționale pentru Protecția Consumatorilor (ANPC, anpc.ro) sau puteți recurge la o entitate de soluționare alternativă a litigiilor, potrivit Legii nr. 140/2015.`,
          `Platforma europeană SOL (soluționarea online a litigiilor) a fost închisă la 20 iulie 2025, prin Regulamentul (UE) 2024/3228, așa că nu mai există o adresă la care să vă îndrumăm acolo. Lista entităților de soluționare din statele membre este publicată de Comisia Europeană la ${REDRESS.euDisputeBodies}.`,
          `Litigiile nesoluționate amiabil sunt de competența instanțelor române.`,
        ],
      },
      {
        heading: "15. Contact",
        body: [
          `${COMPANY.legalName}`,
          `${COMPANY.registeredOffice}`,
          `CUI ${COMPANY.taxId} · ${COMPANY.tradeRegisterNumber}`,
          `E-mail: ${COMPANY.email} · Telefon: ${COMPANY.phone}`,
        ],
      },
    ],
  },

  en: {
    title: "Terms and Conditions",
    intro: `Please read this document before creating an account. By ticking the acceptance box during registration you enter into a contract with us on the terms below. This English text is a convenience translation; the Romanian version governs.`,
    sections: [
      {
        heading: "1. Who we are",
        body: [
          `The ${COMPANY.brandName} platform (the “Platform”) is operated by ${identity.en} (“we”, “the Platform Provider”).`,
          `For any question about this document, write to us at ${COMPANY.email}.`,
        ],
      },
      {
        heading: "2. Definitions",
        body: [
          {
            list: [
              `**Platform** – the ${COMPANY.brandName} web application, used to publish availability and make appointments.`,
              `**User** – any person who creates an account on the Platform.`,
              `**Customer** – a User who uses the Platform to book appointments with a Service Provider, acting as a consumer.`,
              `**Service Provider** – a professional User (self-employed or a company) who publishes services and working hours on the Platform and receives bookings.`,
              `**Booking** – an appointment made by a Customer with a Service Provider through the Platform.`,
              `**User Content** – data entered by a User: service names, prices, descriptions, images, working hours.`,
            ],
          },
        ],
      },
      {
        heading: "3. Subject matter and our role",
        body: [
          `We provide a technical tool through which Service Providers manage their schedule and receive bookings, and Customers find and book available slots.`,
          `**We do not perform the services booked through the Platform and we are not a party to the contract between a Customer and a Service Provider.** The booked service is performed solely by the Service Provider, at their own responsibility. We do not warrant the quality, legality, safety or price of the services published by Service Providers, and we are not liable for their non-performance or defective performance.`,
          `Complaints about a service performed go to the Service Provider directly. Complaints about how the Platform works come to us.`,
          `We therefore have **two distinct roles**, and the distinction decides who is liable and who invoices: towards bookings we are a **technical intermediary**, not a merchant, and towards Platform subscriptions we are the **merchant** — we sell the access, we collect for it and we issue the invoice, in our own name. We are not a marketplace: no third-party goods or services are sold through the Platform and we collect no money on behalf of Service Providers.`,
        ],
      },
      {
        heading: "4. Eligibility and account creation",
        body: [
          `You must be at least 16 years old to create an account. If you are under 16, the account may only be created on your behalf by the holder of parental authority.`,
          `You undertake to provide true, complete and current information and to keep it up to date. An account created with false details may be suspended.`,
          `An account becomes usable only after you confirm your e-mail address via the activation link we send. The link is valid for 24 hours.`,
          `You are responsible for keeping your password confidential and for all activity carried out from your account. If you suspect unauthorised use, tell us immediately at ${COMPANY.email}.`,
          `An account is personal. Do not transfer it or make it available to anyone else.`,
        ],
      },
      {
        heading: "5. Prohibited uses",
        body: [
          `You undertake not to:`,
          {
            list: [
              `upload unlawful, misleading or defamatory content, or content infringing anyone's rights;`,
              `use the Platform to make false or bulk bookings, or to block other users' availability;`,
              `attempt to access accounts, data or areas of the Platform you have no right to access;`,
              `scrape data from the Platform, circumvent security measures or technical limits;`,
              `upload harmful code or interfere with the operation of the Platform;`,
              `use other users' data for any purpose other than the one it was made available to you for.`,
            ],
          },
          `Breach of these obligations may lead to suspension or closure of the account, under section 12.`,
        ],
      },
      {
        heading: "6. Bookings — provisions for Customers",
        body: [
          `A booking made through the Platform is a request addressed to the Service Provider, who may confirm, change or decline it according to their own policy.`,
          `Cancellation, rescheduling, payment terms and any penalties are set by each Service Provider and communicated to you by them. We recommend checking them before booking.`,
          `Cancelling a booking through the Platform, where that option is available, does not release you from obligations towards the Service Provider if their policy provides otherwise.`,
        ],
      },
      {
        heading:
          "7. Subscriptions and payments — provisions for Service Providers",
        body: [
          `Service Providers access the Platform's features on a subscription basis, on the plan and at the price displayed at the time of subscribing. Prices are in EUR and exclude VAT.`,
          `Each plan includes limits (number of resources, services, bookings). When a limit is reached the operation is refused with an explicit message, and the plan can be changed at any time.`,
          `Payment is made online by card, through ${PAYMENT_PROCESSOR.name}. The price is listed in EUR excluding VAT and is **charged in Romanian lei**, at the National Bank rate of the day of payment and VAT included; the exact amount in lei, the rate used and its date are shown to you in the application before you are sent to the card form. Card details never reach us — they are entered on the processor's own page.`,
          `The invoice is issued by ${COMPANY.legalName} and sent to your account's e-mail address automatically, as a PDF. You can find it at any time in the Billing section of your account.`,
          `A subscription is paid for one period and **does not renew automatically** at the end of it. We take no money you have not started yourself: to continue, you begin a new payment from your account. Access to paid features runs until the date shown there.`,
          `Stopping requires no notice and costs nothing — you let the paid period run out. **Amounts paid for a period that has started are not refunded**, because the service was made available for the whole of that period. The right of withdrawal in section 9 and any period during which the service was unavailable through our fault are the exceptions.`,
          `Failure to pay by the due date may result in restricted access to paid features, with data retained for a reasonable period before deletion.`,
        ],
      },
      {
        heading:
          "8. Processing of the Service Provider's customer data (processor clauses)",
        body: [
          `This section applies only to Service Providers and constitutes the processing agreement required by art. 28 of Regulation (EU) 2016/679 (“GDPR”).`,
          `As regards the data of people who book with you, **you are the controller and we act as processor**. You determine the purposes of the processing and are responsible for having a legal basis for the data you enter or send us, including data you add manually on behalf of your own customers.`,
          `In that capacity we undertake to:`,
          {
            list: [
              `process the data only to provide you with the Platform and only on your instructions;`,
              `ensure confidentiality, through persons bound by a duty of confidentiality;`,
              `apply appropriate technical and organisational measures under art. 32 GDPR;`,
              `not engage further sub-processors without informing you in advance and giving you the opportunity to object; the current list of sub-processors is available on request;`,
              `assist you, within reason, in responding to data subject requests and in meeting your security and impact-assessment obligations;`,
              `notify you without undue delay of any personal data breach we become aware of;`,
              `on termination, delete or return the data at your choice, except where law requires us to keep it;`,
              `make available the information needed to demonstrate compliance with these obligations.`,
            ],
          },
          `The duration of the processing is the duration of the contract. The categories of data and data subjects are those determined by use of the Platform: identification and contact details of your customers, and booking history.`,
          `The sub-processors we use as at this version are those listed in the Privacy Policy, point 5, each with its role and the place of processing. We tell you before adding another and you may object.`,
        ],
      },
      {
        heading: "9. Right of withdrawal — for consumers",
        body: [
          `If you are a consumer, you have the right to withdraw from the distance contract concluded with us within 14 days of its conclusion, without giving reasons, under Romanian Government Emergency Ordinance no. 34/2014.`,
          `**You withdraw online**, using the form at ${REDRESS.withdrawalPath} — the facility we provide under art. 11^1 of OUG no. 34/2014. You receive an e-mail confirmation immediately, stating the date we recorded the request.`,
          `An unequivocal notice sent to ${COMPANY.email}, or the model form annexed to the ordinance, remains equally valid. We do not ask you to justify the decision.`,
          `We refund the amount to the same card within at most 14 days of receiving the request.`,
          `Note: if you request that performance begin before the 14 days expire and confirm that you understand you will lose the right of withdrawal once the service has been fully performed, the right lapses upon full performance, under art. 16(a) of OUG no. 34/2014.`,
          `Creating a free account involves no payment; withdrawal in that case amounts to deleting the account, which you may request at any time.`,
        ],
      },
      {
        heading: "10. Intellectual property",
        body: [
          `The Platform, its code, structure, name and graphic elements belong to us or are used under licence. You acquire no rights in them by using the Platform, beyond the right to use it under these Terms.`,
          `Content you upload remains yours. You grant us a non-exclusive licence, limited to what is necessary to host, display and transmit that content within the Platform, for as long as you keep it uploaded.`,
          `You declare that you hold the necessary rights in the content you upload, including images.`,
        ],
      },
      {
        heading: "11. Availability and limitation of liability",
        body: [
          `We make reasonable efforts to keep the Platform available and working, but we do not warrant uninterrupted or error-free operation. We may suspend access temporarily for maintenance, giving advance notice where possible.`,
          `We are not liable for loss arising from: services performed by Service Providers; content uploaded by users; use of your account by someone who obtained your password through your own fault; or causes not attributable to us, including communications network failures.`,
          `Nothing in this document limits our liability where the law does not permit limitation, including for loss caused intentionally or by gross negligence, and nothing affects the rights consumer protection law grants to consumers.`,
        ],
      },
      {
        heading: "12. Suspension and termination",
        body: [
          `You may close your account at any time by requesting it at ${COMPANY.email}.`,
          `We may suspend or close an account that breaches section 5, where use harms other users or the Platform, or where we are legally required to. Where circumstances allow, we notify you first and give you the chance to put things right.`,
          `On termination, data is deleted or anonymised in accordance with the Privacy Policy, except where law requires us to keep it.`,
        ],
      },
      {
        heading: "13. Changes to these Terms",
        body: [
          `We may change these Terms, for example when we add features or when legislation changes. Each version carries a number and a date, shown at the top of the document.`,
          `We notify substantial changes by e-mail or by an in-app notice at least 15 days before they take effect. If you disagree, you may close your account before that date; continuing to use the Platform after the effective date means accepting the new version.`,
          `We keep a record of the version you accepted at registration and when you accepted it.`,
        ],
      },
      {
        heading: "14. Governing law and dispute resolution",
        body: [
          `These Terms are governed by Romanian law. If you are a consumer, you keep the benefit of the mandatory provisions of the country of your habitual residence.`,
          `You can always come to us directly at ${COMPANY.email}; we aim to respond within 30 days.`,
          `If you are a consumer, you may contact the Romanian National Authority for Consumer Protection (ANPC, anpc.ro) or use an alternative dispute resolution body under Romanian Law no. 140/2015.`,
          `The European ODR platform closed on 20 July 2025 under Regulation (EU) 2024/3228, so there is no longer an address there to point you to. The Commission publishes the list of dispute resolution bodies in the Member States at ${REDRESS.euDisputeBodies}.`,
          `Disputes not settled amicably fall to the competent Romanian courts.`,
        ],
      },
      {
        heading: "15. Contact",
        body: [
          `${COMPANY.legalName}`,
          `${COMPANY.registeredOffice}`,
          `Tax ID ${COMPANY.taxId} · ${COMPANY.tradeRegisterNumber}`,
          `E-mail: ${COMPANY.email} · Telephone: ${COMPANY.phone}`,
        ],
      },
    ],
  },
};

export const privacyContent = {
  lastUpdated: PRIVACY_LAST_UPDATED,
  ro: {
    title: "Politica de Confidențialitate",
    intro: `Acest document explică ce date personale prelucrăm, în ce scopuri și ce drepturi aveți. Este informarea cerută de art. 13 din Regulamentul (UE) 2016/679 („GDPR”).`,
    sections: [
      {
        heading: "1. Operatorul datelor",
        body: [
          `Operatorul este ${identity.ro}.`,
          `Pentru orice chestiune privind datele personale ne puteți scrie la ${dataContact.ro}.`,
          COMPANY.dpoEmail
            ? `Am desemnat un responsabil cu protecția datelor, care poate fi contactat la ${COMPANY.dpoEmail}.`
            : `Nu am desemnat un responsabil cu protecția datelor: nu ne aflăm în niciunul dintre cazurile în care art. 37 GDPR impune desemnarea unuia. Pentru orice chestiune legată de datele dumneavoastră, ne scrieți la adresa de mai sus.`,
        ],
      },
      {
        heading: "2. Cele două roluri pe care le avem",
        body: [
          `**Ca operator**, prelucrăm datele contului dumneavoastră: cele pe care ni le dați la înregistrare și cele generate de folosirea Platformei.`,
          `**Ca împuternicit**, prelucrăm datele clienților unui Furnizor de Servicii în numele acestuia. În acel raport, Furnizorul de Servicii este operatorul, iar dumneavoastră, ca persoană care rezervă, vă puteți adresa direct lui. Clauzele acestei împuterniciri sunt în secțiunea 8 din Termeni și Condiții.`,
        ],
      },
      {
        heading: "3. Ce date prelucrăm",
        body: [
          {
            list: [
              `**Date de identificare și contact:** nume, prenume, adresă de e-mail; telefon, dacă îl furnizați.`,
              `**Date de autentificare:** parola, păstrată exclusiv sub formă protejată, plus momentul activării contului.`,
              `**Dovada acceptării Termenilor:** versiunea acceptată și momentul acceptării.`,
              `**Date privind rezervările:** serviciul, data și ora, furnizorul, starea rezervării, observațiile pe care le adăugați.`,
              `**Date de organizație, pentru Furnizorii de Servicii:** denumire, adresă, program de lucru, servicii, prețuri, membri ai echipei, planul de abonament.`,
              `**Date tehnice:** adresa IP și date de jurnal necesare securității și funcționării.`,
              `**Date de marketing, numai dacă acceptați cookie-urile de marketing:** paginile vizitate pe site-ul nostru, adresa IP, informații despre browser și dispozitiv și identificatorii din cookie-urile Meta Pixel. Detaliile sunt în Politica de cookie-uri.`,
            ],
          },
          `Nu solicităm și nu dorim să primim categorii speciale de date (art. 9 GDPR) — de exemplu date privind sănătatea. Nu introduceți astfel de date în câmpurile libere.`,
        ],
      },
      {
        heading: "4. Scopurile și bazele legale",
        body: [
          {
            list: [
              `**Crearea și administrarea contului, furnizarea Platformei** — executarea contractului, art. 6(1)(b) GDPR.`,
              `**Confirmarea adresei de e-mail și recuperarea parolei** — executarea contractului, art. 6(1)(b).`,
              `**Păstrarea dovezii acceptării Termenilor** — obligația de a putea demonstra acordul, art. 6(1)(c) și art. 7(1).`,
              `**Facturarea abonamentelor și evidențele contabile** — obligație legală, art. 6(1)(c).`,
              `**Securitatea Platformei, prevenirea abuzurilor** — interes legitim, art. 6(1)(f).`,
              `**Comunicări comerciale**, dacă v-ați abonat separat — consimțământ, art. 6(1)(a), revocabil oricând.`,
              `**Măsurarea și țintirea reclamelor noastre de pe Facebook și Instagram (Meta Pixel)** — consimțământ, art. 6(1)(a) GDPR și art. 4 alin. (5) din Legea nr. 506/2004, cerut prin bannerul de cookie-uri și revocabil oricând din „Setări cookie-uri”.`,
              `**Apărarea drepturilor în caz de litigiu** — interes legitim, art. 6(1)(f).`,
            ],
          },
        ],
      },
      {
        heading: "5. Cui divulgăm datele",
        body: [
          `Datele nu se vând și nu se închiriază. Le divulgăm doar:`,
          {
            list: [
              `Furnizorului de Servicii la care faceți o rezervare, în măsura necesară efectuării ei;`,
              `prestatorilor care ne susțin tehnic — găzduire, transmiterea e-mailurilor, procesarea plăților — pe bază de contract de prelucrare;`,
              `către Meta, numai dacă acceptați cookie-urile de marketing — vedeți mai jos;`,
              `autorităților, când legea ne obligă;`,
              `consultanților noștri, în măsura necesară apărării unui drept.`,
            ],
          },
          {
            list: SUBPROCESSORS.map(
              (p) =>
                `**${p.name}** — ${p.role.ro}; prelucrare în ${p.location.ro}.`,
            ),
          },
          `Niciunul dintre ei nu are dreptul să folosească datele în scopuri proprii.`,
          `**Meta, numai cu acordul dumneavoastră.** Dacă acceptați cookie-urile de marketing, datele colectate prin Meta Pixel ajung la **${META_PIXEL_PARTNER.name}** (${META_PIXEL_PARTNER.address}). ${metaJointControl.ro}`,
          `În afara celor de mai sus, transmitem date doar autorităților, atunci când legea ne obligă.`,
        ],
      },
      {
        heading: "6. Transferuri în afara Spațiului Economic European",
        body: [
          `Un singur prestator de la punctul 5 prelucrează în afara Spațiului Economic European: **Cloudflare, Inc.**, în Statele Unite, prin care trece traficul către site. Transferul se întemeiază pe Cadrul UE-SUA privind confidențialitatea datelor, la care Cloudflare este certificată, și pe clauzele contractuale standard adoptate de Comisia Europeană.`,
          `Ceilalți prestatori prelucrează în Uniunea Europeană sau în România.`,
          `Dacă acceptați cookie-urile de marketing: ${metaTransfer.ro}`,
        ],
      },
      {
        heading: "7. Cât păstrăm datele",
        body: [
          {
            list: [
              `Datele contului: pe durata existenței contului, apoi până la 30 de zile pentru a permite recuperarea unei ștergeri accidentale.`,
              `Dovada acceptării Termenilor: pe durata contractului și 3 ani după încetare, cât timp o pretenție mai poate fi formulată.`,
              `Documentele financiare: 10 ani, potrivit legislației contabile.`,
              `Jurnalele tehnice de securitate: ${LOG_RETENTION_DAYS} de zile.`,
              `Alegerea făcută în bannerul de cookie-uri: ${COOKIE_CHOICE_MONTHS} luni, în browserul dumneavoastră, după care vă întrebăm din nou.`,
              `Cookie-urile Meta Pixel de pe domeniul nostru: ${META_PIXEL_PARTNER.cookieDays} de zile. Datele pe care le primește Meta le păstrează Meta, potrivit politicii sale.`,
            ],
          },
        ],
      },
      {
        heading: "8. Drepturile dumneavoastră",
        body: [
          `Aveți dreptul de acces, rectificare, ștergere, restricționare a prelucrării, portabilitate a datelor și opoziție la prelucrările bazate pe interes legitim. Dacă o prelucrare se face pe bază de consimțământ, îl puteți retrage oricând, fără ca aceasta să afecteze legalitatea prelucrării anterioare.`,
          `Consimțământul pentru cookie-urile de marketing îl retrageți din linkul „Setări cookie-uri” din subsolul paginilor, la fel de simplu cum l-ați dat.`,
          `Vă puteți exercita drepturile scriindu-ne la ${dataContact.ro}. Răspundem în cel mult o lună de la cerere; termenul poate fi prelungit cu două luni pentru cereri complexe, caz în care vă anunțăm.`,
          `Dacă nu sunteți mulțumit de răspunsul nostru, aveți dreptul de a depune plângere la Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP, dataprotection.ro) și de a vă adresa instanței.`,
        ],
      },
      {
        heading: "9. Cookie-uri și tehnologii similare",
        body: [
          `Folosim cookie-uri și stocarea locală a browserului. Cele esențiale — care vă țin conectat, păstrează limba aleasă și vă memorează alegerea privind cookie-urile — funcționează fără acord, potrivit art. 4 alin. (5) din Legea nr. 506/2004.`,
          `Singurul instrument opțional este **Meta Pixel**, pentru reclamele noastre de pe Facebook și Instagram. Se încarcă numai dacă îl acceptați în bannerul de cookie-uri și rulează ${pixelScope.ro}. Vă puteți retrage acordul oricând din „Setări cookie-uri”.`,
          `Lista completă — fiecare cookie, cine îl pune, la ce servește și cât durează — se află în Politica de cookie-uri (/cookies).`,
        ],
      },
      {
        heading: "10. Securitate",
        body: [
          `Aplicăm măsuri tehnice și organizatorice adecvate: comunicații criptate, parole păstrate exclusiv sub formă protejată, limitarea încercărilor de autentificare, separarea datelor între organizații și acces restrâns la strictul necesar.`,
          `Nicio măsură nu oferă garanție absolută. Dacă are loc o încălcare a securității care vă poate afecta drepturile, vă informăm potrivit art. 34 GDPR și notificăm autoritatea potrivit art. 33.`,
        ],
      },
      {
        heading: "11. Decizii automatizate",
        body: [
          `Nu luăm decizii automatizate care să producă efecte juridice asupra dumneavoastră și nu vă profilăm în acest sens.`,
          `Dacă acceptați cookie-urile de marketing, Meta poate folosi datele colectate pentru a vă afișa reclame potrivite intereselor dumneavoastră, ceea ce înseamnă profilare făcută de Meta. Ea nu produce efecte juridice asupra dumneavoastră, iar după ce vă retrageți acordul Meta nu mai primește date de pe site-ul nostru.`,
        ],
      },
      {
        heading: "12. Modificări ale acestei politici",
        body: [
          `Când modificăm această politică, actualizăm data din capul documentului. Modificările substanțiale vă sunt comunicate prin e-mail sau în aplicație.`,
        ],
      },
    ],
  },

  en: {
    title: "Privacy Policy",
    intro: `This document explains what personal data we process, for what purposes, and what rights you have. It is the information notice required by art. 13 of Regulation (EU) 2016/679 (“GDPR”). The Romanian version governs.`,
    sections: [
      {
        heading: "1. The controller",
        body: [
          `The controller is ${identity.en}.`,
          `For any matter concerning personal data, write to us at ${dataContact.en}.`,
          COMPANY.dpoEmail
            ? `We have appointed a data protection officer, reachable at ${COMPANY.dpoEmail}.`
            : `We have not appointed a data protection officer: none of the cases in which art. 37 GDPR requires one applies to us. For anything concerning your data, write to the address above.`,
        ],
      },
      {
        heading: "2. Our two roles",
        body: [
          `**As controller**, we process your account data: what you give us at registration and what your use of the Platform generates.`,
          `**As processor**, we process a Service Provider's customer data on their behalf. In that relationship the Service Provider is the controller, and you, as the person booking, can address them directly. The clauses governing this are in section 8 of the Terms and Conditions.`,
        ],
      },
      {
        heading: "3. What data we process",
        body: [
          {
            list: [
              `**Identification and contact data:** first and last name, e-mail address; telephone, if you provide it.`,
              `**Authentication data:** your password, stored only in protected form, plus the time your account was activated.`,
              `**Proof of acceptance of the Terms:** the version accepted and when.`,
              `**Booking data:** service, date and time, provider, booking status, any notes you add.`,
              `**Organisation data, for Service Providers:** name, address, working hours, services, prices, team members, subscription plan.`,
              `**Technical data:** IP address and log data needed for security and operation.`,
              `**Marketing data, only if you accept marketing cookies:** the pages you visit on our site, your IP address, browser and device information, and the identifiers in the Meta Pixel cookies. The details are in the Cookie Policy.`,
            ],
          },
          `We do not ask for and do not wish to receive special categories of data (art. 9 GDPR) — health data, for example. Please do not enter such data in free-text fields.`,
        ],
      },
      {
        heading: "4. Purposes and legal bases",
        body: [
          {
            list: [
              `**Creating and administering your account, providing the Platform** — performance of the contract, art. 6(1)(b) GDPR.`,
              `**E-mail confirmation and password recovery** — performance of the contract, art. 6(1)(b).`,
              `**Keeping proof that you accepted the Terms** — the obligation to be able to demonstrate agreement, art. 6(1)(c) and art. 7(1).`,
              `**Subscription billing and accounting records** — legal obligation, art. 6(1)(c).`,
              `**Platform security and abuse prevention** — legitimate interest, art. 6(1)(f).`,
              `**Marketing communications**, if you separately subscribed — consent, art. 6(1)(a), withdrawable at any time.`,
              `**Measuring and targeting our ads on Facebook and Instagram (Meta Pixel)** — consent, art. 6(1)(a) GDPR and art. 4(5) of Romanian Law no. 506/2004, asked for through the cookie banner and withdrawable at any time from "Cookie settings".`,
              `**Defending our rights in a dispute** — legitimate interest, art. 6(1)(f).`,
            ],
          },
        ],
      },
      {
        heading: "5. Who we disclose data to",
        body: [
          `We do not sell or rent data. We disclose it only to:`,
          {
            list: [
              `the Service Provider you book with, to the extent needed to carry out the booking;`,
              `providers who support us technically — hosting, e-mail delivery, payment processing — under a processing agreement;`,
              `Meta, only if you accept marketing cookies — see below;`,
              `authorities, where the law requires it;`,
              `our advisers, to the extent needed to defend a right.`,
            ],
          },
          {
            list: SUBPROCESSORS.map(
              (p) =>
                `**${p.name}** — ${p.role.en}; processing in ${p.location.en}.`,
            ),
          },
          `None of them may use the data for their own purposes.`,
          `**Meta, only with your consent.** If you accept marketing cookies, the data collected through the Meta Pixel reaches **${META_PIXEL_PARTNER.name}** (${META_PIXEL_PARTNER.addressEn}). ${metaJointControl.en}`,
          `Beyond the above, we disclose data only to public authorities, where the law requires it.`,
        ],
      },
      {
        heading: "6. Transfers outside the European Economic Area",
        body: [
          `One provider in point 5 processes outside the European Economic Area: **Cloudflare, Inc.**, in the United States, through which traffic to the site passes. The transfer relies on the EU-U.S. Data Privacy Framework, under which Cloudflare is certified, and on the standard contractual clauses adopted by the European Commission.`,
          `The remaining providers process within the European Union or in Romania.`,
          `If you accept marketing cookies: ${metaTransfer.en}`,
        ],
      },
      {
        heading: "7. How long we keep data",
        body: [
          {
            list: [
              `Account data: for as long as the account exists, then up to 30 days to allow recovery from accidental deletion.`,
              `Proof of acceptance of the Terms: for the duration of the contract and 3 years after it ends, while a claim can still be brought.`,
              `Financial records: 10 years, under accounting legislation.`,
              `Technical security logs: ${LOG_RETENTION_DAYS} days.`,
              `Your answer to the cookie banner: ${COOKIE_CHOICE_MONTHS} months, in your browser, after which we ask again.`,
              `The Meta Pixel cookies on our domain: ${META_PIXEL_PARTNER.cookieDays} days. The data Meta receives is kept by Meta, under its own policy.`,
            ],
          },
        ],
      },
      {
        heading: "8. Your rights",
        body: [
          `You have the right of access, rectification, erasure, restriction of processing, data portability, and objection to processing based on legitimate interest. Where processing relies on consent, you may withdraw it at any time, without affecting the lawfulness of processing carried out beforehand.`,
          `You withdraw consent to marketing cookies from the "Cookie settings" link at the bottom of our pages, as easily as you gave it.`,
          `Exercise your rights by writing to ${dataContact.en}. We respond within one month; the deadline may be extended by two months for complex requests, in which case we will tell you.`,
          `If you are not satisfied with our response, you have the right to lodge a complaint with the Romanian data protection authority (ANSPDCP, dataprotection.ro) and to go to court.`,
        ],
      },
      {
        heading: "9. Cookies and similar technologies",
        body: [
          `We use cookies and the browser's local storage. The essential ones — which keep you signed in, remember your language and remember your cookie choice — work without consent, under art. 4(5) of Romanian Law no. 506/2004.`,
          `The only optional tool is the **Meta Pixel**, for our ads on Facebook and Instagram. It loads only if you accept it in the cookie banner, and runs ${pixelScope.en}. You can withdraw your consent at any time from "Cookie settings".`,
          `The full list — every cookie, who sets it, what it is for and how long it lasts — is in the Cookie Policy (/cookies).`,
        ],
      },
      {
        heading: "10. Security",
        body: [
          `We apply appropriate technical and organisational measures: encrypted communications, passwords stored only in protected form, rate-limited authentication attempts, data separation between organisations, and access restricted to what is strictly necessary.`,
          `No measure offers an absolute guarantee. If a breach occurs that may affect your rights, we will inform you under art. 34 GDPR and notify the authority under art. 33.`,
        ],
      },
      {
        heading: "11. Automated decision-making",
        body: [
          `We do not take automated decisions producing legal effects concerning you, and we do not profile you for that purpose.`,
          `If you accept marketing cookies, Meta may use the data collected to show you ads matched to your interests, which is profiling carried out by Meta. It produces no legal effects concerning you, and once you withdraw your consent Meta receives no further data from our site.`,
        ],
      },
      {
        heading: "12. Changes to this policy",
        body: [
          `When we change this policy we update the date at the top of the document. Substantial changes are communicated by e-mail or in the application.`,
        ],
      },
    ],
  },
};

/**
 * Politica de livrare.
 *
 * A separate document rather than a paragraph inside the Terms, because
 * NETOPIA's point-of-sale checklist asks for it by name and a reviewer looks
 * for a link called that. What it has to say is short: this is software, so
 * "delivery" is the moment the account unlocks, and that moment is immediate.
 */
export const deliveryContent = {
  ro: {
    title: "Politica de livrare",
    intro: `${COMPANY.brandName} este un serviciu software livrat electronic. Nu expediem bunuri fizice și nu există transport, curier sau termen de livrare în sensul obișnuit — acest document explică ce primiți, când și ce se întâmplă dacă nu primiți.`,
    sections: [
      {
        heading: "1. Ce se livrează",
        body: [
          `Ceea ce cumpărați este accesul, pe durata abonamentului, la funcționalitățile planului ales: pagina publică de rezervări, gestiunea programului și a resurselor, confirmările și memento-urile pe e-mail, rapoartele — în limitele planului, așa cum sunt afișate pe pagina de prețuri la momentul plății.`,
          `Nu se livrează niciun bun fizic și nu se expediază nimic prin poștă sau curier.`,
        ],
      },
      {
        heading: "2. Când se livrează",
        body: [
          `**Imediat.** Funcționalitățile plătite se deblochează automat în contul dumneavoastră în momentul în care procesatorul ne confirmă plata — de regulă în câteva secunde de la finalizarea plății cu cardul.`,
          `Nu există un termen de livrare de zile și nici un interval de expediere: dacă plata a trecut, accesul există.`,
        ],
      },
      {
        heading: "3. Factura",
        body: [
          `Factura este emisă de ${COMPANY.legalName} și vă este trimisă automat, în PDF, pe adresa de e-mail a contului. O puteți descărca oricând din secțiunea Facturare.`,
        ],
      },
      {
        heading: "4. Dacă accesul nu apare",
        body: [
          `Confirmarea plății ne vine de la procesator, nu din browserul dumneavoastră, așa că accesul apare chiar dacă închideți pagina imediat după plată. Dacă totuși, după câteva minute, contul nu arată abonamentul plătit, scrieți-ne la ${COMPANY.email} cu adresa contului și ora plății.`,
          `Dacă plata a fost încasată și accesul nu poate fi acordat din motive care țin de noi, vă restituim integral suma.`,
        ],
      },
    ],
  },
  en: {
    title: "Delivery Policy",
    intro: `${COMPANY.brandName} is software delivered electronically. We ship no physical goods, so there is no carrier, no shipping and no delivery time in the usual sense — this document sets out what you receive, when, and what happens if you do not.`,
    sections: [
      {
        heading: "1. What is delivered",
        body: [
          `What you buy is access, for the duration of the subscription, to the features of the plan you chose: the public booking page, schedule and resource management, e-mail confirmations and reminders, reports — within that plan's limits as displayed on the pricing page at the time of payment.`,
          `No physical goods are delivered and nothing is sent by post or courier.`,
        ],
      },
      {
        heading: "2. When it is delivered",
        body: [
          `**Immediately.** Paid features unlock in your account automatically the moment the processor confirms your payment to us — usually within seconds of completing the card payment.`,
          `There is no delivery window measured in days and no dispatch period: if the payment went through, the access is there.`,
        ],
      },
      {
        heading: "3. The invoice",
        body: [
          `The invoice is issued by ${COMPANY.legalName} and sent to your account's e-mail address automatically, as a PDF. You can download it at any time from the Billing section.`,
        ],
      },
      {
        heading: "4. If the access does not appear",
        body: [
          `Payment confirmation reaches us from the processor rather than from your browser, so access appears even if you close the page straight after paying. If, after a few minutes, your account still does not show the subscription, write to ${COMPANY.email} with the account address and the time of payment.`,
          `If a payment was taken and access cannot be granted for reasons on our side, we refund it in full.`,
        ],
      },
    ],
  },
};

/**
 * Politica de anulare.
 *
 * Two different things share the word "cancellation" and keeping them apart is
 * the whole job of this document: stopping a subscription, which anyone may do
 * at any time and which refunds nothing, and the statutory right of withdrawal,
 * which is time-limited and does refund. Somebody who reads only one of the two
 * ends up either paying for something they stopped using or believing they lost
 * a right they still have.
 */
export const cancellationContent = {
  ro: {
    title: "Politica de anulare",
    intro: `Acest document explică cum vă opriți abonamentul la ${COMPANY.brandName}, ce se întâmplă cu banii deja plătiți și în ce situații aveți dreptul la restituire.`,
    sections: [
      {
        heading: "1. Vă puteți opri oricând",
        body: [
          `Abonamentul se plătește pentru o perioadă și **nu se reînnoiește automat**. Nu vă reținem cardul și nu inițiem nicio plată în locul dumneavoastră: pentru a continua după încheierea perioadei, porniți dumneavoastră o plată nouă din cont.`,
          `Prin urmare, „anularea” nu cere nicio acțiune și niciun preaviz — dacă nu mai plătiți, abonamentul se încheie singur la data afișată în secțiunea Facturare.`,
        ],
      },
      {
        heading: "2. Ce se întâmplă la încheierea perioadei",
        body: [
          `Contul rămâne al dumneavoastră și datele rămân la locul lor. Se închid funcționalitățile plătite ale planului; pagina publică de rezervări și istoricul rămân accesibile în limitele planului gratuit.`,
          `Vă puteți întoarce oricând, plătind o nouă perioadă, fără să pierdeți ce aveați configurat.`,
        ],
      },
      {
        heading: "3. Restituirea sumelor",
        body: [
          `**Sumele plătite pentru o perioadă deja începută nu se restituie.** Motivul este simplu: serviciul v-a fost pus la dispoziție, integral, pentru toată perioada respectivă, din prima secundă după plată.`,
          `Există două excepții, în care restituim integral:`,
          {
            list: [
              `exercitarea dreptului legal de retragere, în condițiile de la punctul 4;`,
              `situația în care serviciul plătit nu v-a fost pus la dispoziție din motive care țin de noi.`,
            ],
          },
          `Restituirea se face pe același card cu care s-a plătit, în cel mult 14 zile de la data la care dreptul la restituire este stabilit.`,
        ],
      },
      {
        heading: "4. Dreptul de retragere, pentru consumatori",
        body: [
          `Dacă sunteți consumator — persoană fizică ce cumpără în afara activității profesionale — aveți dreptul de a vă retrage din contract în 14 zile de la încheierea lui, fără să vă justificați decizia, potrivit OUG nr. 34/2014.`,
          `**Îl exercitați online**, din formularul aflat la ${REDRESS.withdrawalPath}, sau printr-o notificare neechivocă trimisă la ${COMPANY.email}. Ambele sunt valabile; formularul este doar mai rapid și vă lasă o confirmare scrisă.`,
          `Atenție la o consecință a livrării imediate: dacă ne cereți să începem furnizarea serviciului înainte de expirarea celor 14 zile și confirmați că înțelegeți acest lucru, dreptul de retragere se stinge în momentul în care serviciul a fost executat integral — art. 16 lit. a) din OUG nr. 34/2014.`,
        ],
      },
      {
        heading: "5. Anularea unei programări — altceva",
        body: [
          `Anularea unei **programări** făcute prin ${COMPANY.brandName} la o afacere care folosește platforma nu are legătură cu acest document. Condițiile acolo sunt stabilite de afacerea respectivă, iar reclamațiile i se adresează direct. Vedeți secțiunile 3 și 6 din Termeni și Condiții.`,
        ],
      },
      {
        heading: "6. Cum ne contactați",
        body: [
          `${COMPANY.legalName} · ${COMPANY.email} · ${COMPANY.phone}`,
          `Răspundem în cel mult 30 de zile, de regulă mult mai repede.`,
        ],
      },
    ],
  },
  en: {
    title: "Cancellation Policy",
    intro: `This document explains how to stop your ${COMPANY.brandName} subscription, what happens to money already paid, and when you are entitled to a refund.`,
    sections: [
      {
        heading: "1. You can stop at any time",
        body: [
          `A subscription is paid for one period and **does not renew automatically**. We do not keep your card on file and we start no payment on your behalf: to continue after the period ends, you begin a new payment from your account.`,
          `So "cancelling" requires no action and no notice — if you stop paying, the subscription ends by itself on the date shown in the Billing section.`,
        ],
      },
      {
        heading: "2. What happens when the period ends",
        body: [
          `The account stays yours and your data stays where it is. The plan's paid features close; the public booking page and your history remain available within the limits of the free plan.`,
          `You can come back at any time by paying for a new period, without losing what you had set up.`,
        ],
      },
      {
        heading: "3. Refunds",
        body: [
          `**Amounts paid for a period that has already started are not refunded.** The reason is simple: the service was made available to you in full, for the whole of that period, from the first second after payment.`,
          `There are two exceptions, in which we refund in full:`,
          {
            list: [
              `exercise of the statutory right of withdrawal, on the terms in point 4;`,
              `where the service paid for was not made available to you for reasons on our side.`,
            ],
          },
          `Refunds are made to the card used for the payment, within at most 14 days of the date the entitlement is established.`,
        ],
      },
      {
        heading: "4. Right of withdrawal, for consumers",
        body: [
          `If you are a consumer — an individual buying outside a trade or profession — you have the right to withdraw from the contract within 14 days of concluding it, without giving reasons, under Romanian Government Emergency Ordinance no. 34/2014.`,
          `**You exercise it online**, through the form at ${REDRESS.withdrawalPath}, or by an unequivocal notice sent to ${COMPANY.email}. Both are valid; the form is simply faster and leaves you a written confirmation.`,
          `Note one consequence of immediate delivery: if you ask us to begin providing the service before the 14 days expire and confirm that you understand this, the right of withdrawal lapses at the moment the service has been performed in full — art. 16(a) of OUG no. 34/2014.`,
        ],
      },
      {
        heading: "5. Cancelling a booking — a different thing",
        body: [
          `Cancelling an **appointment** made through ${COMPANY.brandName} with a business that uses the platform has nothing to do with this document. The terms there are set by that business, and complaints go to it directly. See sections 3 and 6 of the Terms and Conditions.`,
        ],
      },
      {
        heading: "6. How to reach us",
        body: [
          `${COMPANY.legalName} · ${COMPANY.email} · ${COMPANY.phone}`,
          `We reply within 30 days at the latest, usually a good deal sooner.`,
        ],
      },
    ],
  },
};

/**
 * Politica de cookie-uri.
 *
 * Its own document since the Meta Pixel arrived: until then the whole subject
 * fit in one section of the Privacy Policy ("we use no cookies"), and that
 * section now points here. The banner and the footer link here too.
 *
 * The essential list has to be true, not complete-looking — every key in it is
 * one the code actually writes (grep for it before adding or removing a row),
 * and a key the code starts writing comes here in the same change. What the
 * pixel may see is fixed by TRACKED_PAGES in utils/metaPixel.js, which
 * `pixelScope` above describes.
 */
export const cookiesContent = {
  lastUpdated: PRIVACY_LAST_UPDATED,
  ro: {
    title: "Politica de cookie-uri",
    intro: `Acest document explică ce cookie-uri și tehnologii similare folosește ${COMPANY.brandName}, la ce servesc, cine le pune și cum vă dați sau vă retrageți acordul. Completează Politica de Confidențialitate, care descrie restul prelucrărilor de date.`,
    sections: [
      {
        heading: "1. Ce sunt cookie-urile",
        body: [
          `Un cookie este un fișier mic pe care un site îl salvează în browserul dumneavoastră și pe care îl poate citi la vizitele următoare. Stocarea locală a browserului („localStorage”) și scripturile care trimit unui terț date despre vizită, precum pixelii, funcționează asemănător, iar legea le tratează la fel. În acest document, „cookie-uri” înseamnă toate acestea.`,
          `Potrivit art. 4 alin. (5) din Legea nr. 506/2004, fără acordul dumneavoastră putem folosi doar ce este strict necesar pentru a vă oferi serviciul pe care l-ați cerut. Pentru orice altceva vă cerem acordul înainte.`,
        ],
      },
      {
        heading: "2. Cum vă cerem acordul",
        body: [
          `La prima vizită pe paginile de prezentare ${COMPANY.brandName} afișăm un banner cu trei opțiuni la fel de vizibile: **Acceptă toate**, **Personalizează** și **Refuză opționalele**. Până alegeți ceva sunt active doar cookie-urile esențiale — lipsa unui răspuns nu înseamnă acord.`,
          `Alegerea se păstrează în browserul dumneavoastră ${COOKIE_CHOICE_MONTHS} luni, după care vă întrebăm din nou. Vă întrebăm din nou și mai devreme dacă adăugăm o categorie sau un partener nou.`,
          `**Vă retrageți acordul la fel de simplu cum l-ați dat:** din linkul „Setări cookie-uri” aflat în subsolul paginilor de prezentare sau din butonul de mai sus. Retragerea nu afectează legalitatea prelucrării făcute înainte.`,
          `Refuzul nu are nicio consecință asupra folosirii site-ului: toate funcțiile rămân disponibile.`,
        ],
      },
      {
        heading: "3. Cookie-uri esențiale — fără acord",
        body: [
          `Sunt toate ale noastre, niciunul nu poate fi citit de alte site-uri și niciunul nu servește la a vă urmări:`,
          {
            list: [
              `**ep_session** (cookie) — vă ține conectat când bifați „Ține-mă minte” la autentificare. Nu poate fi citit de scripturi și circulă doar către serverul de autentificare. Expiră după 7 zile fără utilizare sau la deconectare.`,
              `**ep_google** (cookie) — există doar cât durează autentificarea cu „Continuă cu Google” și leagă răspunsul Google de browserul dumneavoastră, ca nimeni altcineva să nu-l poată folosi. Expiră după 10 minute.`,
              `**accessToken** (stocare locală) — sesiunea de autentificare. Se creează la autentificare și se șterge la deconectare; încetează oricum să fie valabilă după 2 ore sau, dacă ați bifat „Ține-mă minte”, după 15 minute, când se reînnoiește automat.`,
              `**USER_EMAIL** (stocare locală) — adresa de e-mail a contului, afișată în panoul de administrare. Se salvează și se șterge odată cu sesiunea.`,
              `**selectedPlan** (stocare locală) — planul ales pe pagina de prețuri, ca să nu-l alegeți din nou la înregistrare. Se șterge după ce vă creați organizația.`,
              `**i18nextLng** (stocare locală) — limba site-ului: cea aleasă de dumneavoastră sau, până alegeți una, limba paginii pe care ați deschis-o (de regulă româna). Rămâne până ștergeți datele site-ului.`,
              `**quickBooking.lastServiceId** (stocare locală, doar în panoul afacerilor) — ultimul serviciu ales la programarea rapidă, ca să fie preselectat data viitoare. Rămâne până ștergeți datele site-ului.`,
              `**cookieConsent** (stocare locală) — răspunsul dumneavoastră la bannerul de cookie-uri și data lui. ${COOKIE_CHOICE_MONTHS} luni.`,
            ],
          },
        ],
      },
      {
        heading: "4. Cookie-uri de marketing — numai cu acordul dumneavoastră",
        body: [
          `Folosim un singur instrument de marketing: **Meta Pixel**, furnizat de **${META_PIXEL_PARTNER.name}** (${META_PIXEL_PARTNER.address}). Ne arată dacă reclamele noastre de pe Facebook și Instagram aduc vizitatori și înregistrări și ne permite să le arătăm persoanelor care au vizitat site-ul sau care seamănă cu ele.`,
          `Pixelul se încarcă doar după ce apăsați „Acceptă toate” sau activați „Marketing” în preferințe. Până atunci browserul dumneavoastră nu contactează deloc serverele Meta.`,
          `**Unde rulează:** ${pixelScope.ro}.`,
          `**Ce primește Meta:** adresa paginii vizitate și a celei de pe care ați venit, adresa IP, informații despre browser și dispozitiv, momentul vizitei și identificatorii din cookie-urile de mai jos. Dacă vă creați un cont de afacere din formularul de înregistrare, Meta primește în plus, o singură dată, evenimentul „înregistrare finalizată” (CompleteRegistration): numai faptul că s-a creat un cont de afacere, ca să aflăm ce reclame aduc înregistrări. Nu îi transmitem numele, adresa de e-mail, numărul de telefon și nimic din ce scrieți în formulare.`,
          {
            list: [
              `**_fbp** (cookie pus de Meta Pixel pe domeniul nostru) — deosebește browserul dumneavoastră de altele, ca vizitele să poată fi legate între ele. ${META_PIXEL_PARTNER.cookieDays} de zile.`,
              `**_fbc** (cookie pus de Meta Pixel pe domeniul nostru) — apare doar când ajungeți pe site dintr-o reclamă Facebook sau Instagram și păstrează identificatorul acelui clic. ${META_PIXEL_PARTNER.cookieDays} de zile.`,
              `**Cookie-urile Meta de pe facebook.com** (de exemplu **fr**) — Meta le citește sau le pune dacă sunteți conectat la Facebook ori Instagram în același browser sau dacă browserul permite cookie-uri de la terți. Le gestionează Meta, potrivit politicii sale, de regulă până la ${META_PIXEL_PARTNER.cookieDays} de zile.`,
            ],
          },
          `**Cine răspunde:** ${metaJointControl.ro}`,
          `**Transfer în afara Spațiului Economic European:** ${metaTransfer.ro}`,
          `Ce reclame vă arată Meta puteți controla și din contul dumneavoastră Meta, la ${META_PIXEL_PARTNER.adPreferences}.`,
        ],
      },
      {
        heading: "5. Servicii terțe care nu pun cookie-uri",
        body: [
          `În subsolul paginilor se află sigla NETOPIA Payments, a cărei afișare ne-o cere contractul cu procesatorul de plăți. Imaginea se încarcă de pe serverul NETOPIA (mny.ro), care primește astfel — ca la orice imagine găzduită pe alt site — adresa IP a dispozitivului și adresa site-ului nostru. Nu plasează cookie-uri și nu stochează nimic în browser.`,
          `Butonul „Continuă cu Google” nu încarcă niciun script Google pe paginile noastre: vă trimite pe pagina Google, unde se aplică politica Google privind cookie-urile.`,
          `Formularul de rezervare pe care un Furnizor de Servicii îl include pe propriul site nu stochează nimic în browserul dumneavoastră și nu încarcă Meta Pixel. Plata unui abonament se face pe pagina NETOPIA, unde se aplică politica lor privind cookie-urile.`,
        ],
      },
      {
        heading: "6. Cum controlați cookie-urile",
        body: [
          `Cookie-urile de marketing le porniți și le opriți oricând din „Setări cookie-uri”. Când vă retrageți acordul, oprim pixelul și ștergem cookie-urile _fbp și _fbc de pe domeniul nostru; pe cele de pe facebook.com le puteți șterge doar din browser sau din contul Meta.`,
          `Din setările browserului puteți șterge sau bloca orice cookie și datele site-ului. Dacă le ștergeți pe cele esențiale, veți fi deconectat, site-ul va reveni la limba browserului și vă vom întreba din nou despre cookie-uri.`,
        ],
      },
      {
        heading: "7. Modificări ale acestei politici",
        body: [
          `Când schimbăm această politică actualizăm data din capul ei. Dacă adăugăm o categorie de cookie-uri sau un partener nou, vă cerem din nou acordul, prin banner, înainte de a le folosi.`,
        ],
      },
      {
        heading: "8. Contact",
        body: [
          `Pentru orice întrebare despre cookie-uri sau despre datele dumneavoastră ne scrieți la ${dataContact.ro}. Drepturile pe care le aveți și plângerea la ANSPDCP sunt descrise în Politica de Confidențialitate (/privacy).`,
        ],
      },
    ],
  },
  en: {
    title: "Cookie Policy",
    intro: `This document explains which cookies and similar technologies ${COMPANY.brandName} uses, what they are for, who sets them, and how you give or withdraw consent. It complements the Privacy Policy, which describes the rest of our data processing. The Romanian version governs.`,
    sections: [
      {
        heading: "1. What cookies are",
        body: [
          `A cookie is a small file a website saves in your browser and can read on later visits. The browser's local storage ("localStorage") and scripts that send a third party data about your visit, such as pixels, work in a similar way, and the law treats them alike. In this document, "cookies" means all of them.`,
          `Under art. 4(5) of Romanian Law no. 506/2004, without your consent we may use only what is strictly necessary to provide the service you asked for. For anything else we ask for your consent first.`,
        ],
      },
      {
        heading: "2. How we ask for consent",
        body: [
          `On your first visit to ${COMPANY.brandName}'s own pages we show a banner with three equally visible options: **Accept all**, **Customize** and **Reject optional**. Until you choose, only essential cookies are active — no answer does not mean consent.`,
          `Your choice is kept in your browser for ${COOKIE_CHOICE_MONTHS} months, after which we ask again. We also ask again sooner if we add a new category or partner.`,
          `**You withdraw consent as easily as you gave it:** from the "Cookie settings" link at the bottom of our pages, or from the button above. Withdrawal does not affect the lawfulness of processing carried out before it.`,
          `Refusing has no effect on using the site: every feature remains available.`,
        ],
      },
      {
        heading: "3. Essential cookies — no consent needed",
        body: [
          `They are all our own; none can be read by other websites and none is used to track you:`,
          {
            list: [
              `**ep_session** (cookie) — keeps you signed in when you tick "Keep me signed in" at sign-in. Scripts cannot read it and it travels only to the sign-in server. Expires after 7 days without use, or when you sign out.`,
              `**ep_google** (cookie) — exists only while you sign in with "Continue with Google", and ties Google's answer to your browser so nobody else can use it. Expires after 10 minutes.`,
              `**accessToken** (local storage) — your sign-in session. Created when you sign in and removed when you sign out; it stops being valid after 2 hours in any case or, if you ticked "Keep me signed in", after 15 minutes, when it renews itself.`,
              `**USER_EMAIL** (local storage) — your account's e-mail address, shown in the dashboard. Stored and removed together with the session.`,
              `**selectedPlan** (local storage) — the plan you picked on the pricing page, so you do not have to pick it again when you sign up. Removed once you have created your organisation.`,
              `**i18nextLng** (local storage) — the site's language: the one you chose or, until you choose one, the language of the page you opened (usually Romanian). Stays until you clear the site's data.`,
              `**quickBooking.lastServiceId** (local storage, business dashboard only) — the last service picked in quick booking, so it is preselected next time. Stays until you clear the site's data.`,
              `**cookieConsent** (local storage) — your answer to the cookie banner and its date. ${COOKIE_CHOICE_MONTHS} months.`,
            ],
          },
        ],
      },
      {
        heading: "4. Marketing cookies — only with your consent",
        body: [
          `We use a single marketing tool: the **Meta Pixel**, provided by **${META_PIXEL_PARTNER.name}** (${META_PIXEL_PARTNER.addressEn}). It shows us whether our ads on Facebook and Instagram bring visitors and sign-ups, and lets us show them to people who have visited the site or resemble them.`,
          `The pixel loads only after you press "Accept all" or switch on "Marketing" in the preferences. Until then your browser does not contact Meta's servers at all.`,
          `**Where it runs:** ${pixelScope.en}.`,
          `**What Meta receives:** the address of the page you visit and of the page you came from, your IP address, browser and device information, the time of the visit, and the identifiers in the cookies below. If you create a business account through the sign-up form, Meta also receives, once, a "registration completed" event (CompleteRegistration): only the fact that a business account was created, so that we can tell which ads bring sign-ups. We do not send it your name, e-mail address, phone number or anything you type into a form.`,
          {
            list: [
              `**_fbp** (cookie set by the Meta Pixel on our domain) — tells your browser apart from others, so that visits can be linked. ${META_PIXEL_PARTNER.cookieDays} days.`,
              `**_fbc** (cookie set by the Meta Pixel on our domain) — appears only when you reach the site from a Facebook or Instagram ad, and keeps the identifier of that click. ${META_PIXEL_PARTNER.cookieDays} days.`,
              `**Meta's cookies on facebook.com** (for example **fr**) — Meta reads or sets them if you are signed in to Facebook or Instagram in the same browser, or if your browser allows third-party cookies. Meta manages them under its own policy, usually for up to ${META_PIXEL_PARTNER.cookieDays} days.`,
            ],
          },
          `**Who is responsible:** ${metaJointControl.en}`,
          `**Transfer outside the European Economic Area:** ${metaTransfer.en}`,
          `You can also control which ads Meta shows you from your Meta account, at ${META_PIXEL_PARTNER.adPreferences}.`,
        ],
      },
      {
        heading: "5. Third-party services that set no cookies",
        body: [
          `The footer of our pages carries the NETOPIA Payments logo, which our contract with the payment processor requires us to display. The image is loaded from NETOPIA's server (mny.ro), which therefore receives — as with any image hosted on another website — your device's IP address and our website's address. It sets no cookies and stores nothing in your browser.`,
          `The "Continue with Google" button loads no Google script on our pages: it sends you to Google's own page, where Google's cookie policy applies.`,
          `The booking form a Service Provider embeds on its own website stores nothing in your browser and does not load the Meta Pixel. Subscription payments are completed on NETOPIA's page, where their own cookie policy applies.`,
        ],
      },
      {
        heading: "6. How you control cookies",
        body: [
          `You switch marketing cookies on and off at any time from "Cookie settings". When you withdraw consent, we stop the pixel and delete the _fbp and _fbc cookies from our domain; those on facebook.com you can only delete from your browser or your Meta account.`,
          `Your browser's settings let you delete or block any cookie and the site's data. If you delete the essential ones you will be signed out, the site will go back to your browser's language, and we will ask you about cookies again.`,
        ],
      },
      {
        heading: "7. Changes to this policy",
        body: [
          `When we change this policy we update the date at the top. If we add a new category of cookies or a new partner, we ask for your consent again, through the banner, before using them.`,
        ],
      },
      {
        heading: "8. Contact",
        body: [
          `For any question about cookies or your data, write to us at ${dataContact.en}. Your rights and how to complain to the Romanian data protection authority (ANSPDCP) are set out in the Privacy Policy (/privacy).`,
        ],
      },
    ],
  },
};
